"use client";

import React from "react";
import { ArrowRight, Grid3X3, Layers, Disc, Filter } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import Reveal from "@/components/common/Reveal";
import { useLanguage } from "@/hooks/useLanguage";
import { pick } from "@/lib/i18n";
import { siteImage } from "@/lib/siteImages";
interface ProductCard {
  name: string;
  description: string;
  range?: string;
  image: string;
  imageAlt: string;
  href: string;
  viewText: string;
}
interface SupplyContent {
  eyebrow: string;
  heading: string;
  lead: string;
  viewProducts: string;
  cards: [ProductCard, ProductCard, ProductCard, ProductCard];
}
const copy: Record<"en" | "zh" | "es" | "de" | "fr" | "pt", SupplyContent> = {
  en: {
    eyebrow: "Product Scope",
    heading: "What We Supply",
    lead: "Our range covers four product categories: woven wire mesh, sintered wire mesh, filter components and wedge wire screens.",
    viewProducts: "View Products",
    cards: [{
      name: "Woven Wire Mesh",
      description: "Precision square and Dutch weave mesh for filtration and screening.",
      range: "1–635 mesh · wire 0.020–2.0 mm",
      image: "https://mutualfilter.com/wp-content/uploads/2026/10/plain-dutch-weave-mesh1.jpg",
      imageAlt: "Stainless steel woven wire mesh roll and aperture closeup",
      href: "/WovenWireMeshPage",
      viewText: "View Woven Mesh"
    }, {
      name: "Sintered Wire Mesh",
      description: "Multi-layer sintered laminates for rigid, porous filter media.",
      range: "1–200 µm",
      image: siteImage("7c6fe4-sintered-multilayer-m-7efb42.webp"),
      imageAlt: "Five-layer sintered stainless steel mesh porous plate",
      href: "/SinteredWireMeshPage",
      viewText: "View Sintered Mesh"
    }, {
      name: "Filter Components",
      description: "Discs, cylinders, screen packs and baskets made from mesh.",
      image: siteImage("7c6fe4-fabricated-filter-mes-cfe21c.webp"),
      imageAlt: "Fabricated filter discs, cylindrical tubes and screen packs",
      href: "/FilterComponentsPage",
      viewText: "View Components"
    }, {
      name: "Wedge Wire Screens",
      description: "Profile-wire continuous-slot screens, tubes and panels.",
      range: "Slot 0.02–5.0 mm",
      image: siteImage("7c6fe4-wedge-wire-screen-pan-4aec38.webp"),
      imageAlt: "Continuous slot wedge wire profile screen panel",
      href: "/WedgeWireScreenPage",
      viewText: "View Wedge Wire"
    }]
  },
  zh: {
    eyebrow: "产品范围",
    heading: "我们提供的产品",
    lead: "产品涵盖四大类：编织丝网、烧结丝网、过滤组件及楔形丝筛。",
    viewProducts: "查看全部产品",
    cards: [{
      name: "编织丝网",
      description: "用于过滤与筛分的精密方孔与密纹编织网。",
      range: "1–635 mesh · wire 0.020–2.0 mm",
      image: "https://mutualfilter.com/wp-content/uploads/2026/10/plain-dutch-weave-mesh1.jpg",
      imageAlt: "不锈钢编织丝网卷料及网孔微距特写",
      href: "/WovenWireMeshPage",
      viewText: "查看编织网"
    }, {
      name: "烧结丝网",
      description: "用于高刚性、多孔过滤介质的多层复合烧结网板。",
      range: "1–200 µm",
      image: siteImage("7c6fe4-sintered-multilayer-m-7efb42.webp"),
      imageAlt: "五层不锈钢烧结金属多孔滤板",
      href: "/SinteredWireMeshPage",
      viewText: "查看烧结网"
    }, {
      name: "过滤组件",
      description: "由丝网加工制成的滤片、滤筒、组合网包与滤篮。",
      image: siteImage("7c6fe4-fabricated-filter-mes-cfe21c.webp"),
      imageAlt: "冲压滤片、圆柱形滤筒及组合滤网包组件",
      href: "/FilterComponentsPage",
      viewText: "查看过滤组件"
    }, {
      name: "楔形丝筛",
      description: "连续缝隙型材绕丝筛板、筛管与弧形筛。",
      range: "Slot 0.02–5.0 mm",
      image: siteImage("7c6fe4-wedge-wire-screen-pan-4aec38.webp"),
      imageAlt: "连续缝隙型材绕丝楔形丝筛板",
      href: "/WedgeWireScreenPage",
      viewText: "查看楔形网"
    }]
  },
  es: {
    eyebrow: "Gama de productos",
    heading: "Nuestros productos",
    lead: "Nuestra gama abarca cuatro categorías de producto: malla de alambre tejida, malla sinterizada, componentes de filtración y tamices de alambre en cuña.",
    viewProducts: "Ver productos",
    cards: [{
      name: "Malla de alambre tejida",
      description: "Mallas de tejido cuadrado y holandés de precisión para filtración y cribado.",
      range: "1–635 mesh · wire 0.020–2.0 mm",
      image: "https://mutualfilter.com/wp-content/uploads/2026/10/plain-dutch-weave-mesh1.jpg",
      imageAlt: "Rollo de malla de alambre tejida de acero inoxidable y detalle de abertura",
      href: "/WovenWireMeshPage",
      viewText: "Ver malla tejida"
    }, {
      name: "Malla sinterizada",
      description: "Laminados sinterizados multicapa para medios filtrantes rígidos y porosos.",
      range: "1–200 µm",
      image: siteImage("7c6fe4-sintered-multilayer-m-7efb42.webp"),
      imageAlt: "Placa porosa de malla sinterizada de acero inoxidable de cinco capas",
      href: "/SinteredWireMeshPage",
      viewText: "Ver malla sinterizada"
    }, {
      name: "Componentes de filtración",
      description: "Discos, cilindros, paquetes filtrantes y cestas fabricados a partir de malla.",
      image: siteImage("7c6fe4-fabricated-filter-mes-cfe21c.webp"),
      imageAlt: "Discos filtrantes, tubos cilíndricos y paquetes de tamices fabricados",
      href: "/FilterComponentsPage",
      viewText: "Ver componentes"
    }, {
      name: "Tamices de alambre en cuña",
      description: "Tamices, tubos y paneles de ranura continua con perfil en cuña.",
      range: "Slot 0.02–5.0 mm",
      image: siteImage("7c6fe4-wedge-wire-screen-pan-4aec38.webp"),
      imageAlt: "Panel de tamiz de alambre en cuña con ranura continua",
      href: "/WedgeWireScreenPage",
      viewText: "Ver alambre en cuña"
    }]
  },
  de: {
    eyebrow: "Produktspektrum",
    heading: "Unser Lieferspektrum",
    lead: "Unser Sortiment umfasst vier Produktkategorien: Drahtgewebe, Sinterdrahtgewebe, Filterkomponenten und Spaltsiebe (Keilspaltsiebe).",
    viewProducts: "Produkte anzeigen",
    cards: [{
      name: "Drahtgewebe",
      description: "Präzisions-Quadrat- und Tressengewebe für Filtration und Siebung.",
      range: "1–635 mesh · wire 0.020–2.0 mm",
      image: "https://mutualfilter.com/wp-content/uploads/2026/10/plain-dutch-weave-mesh1.jpg",
      imageAlt: "Edelstahl-Drahtgewebe Rolle und Maschenweiten-Nahaufnahme",
      href: "/WovenWireMeshPage",
      viewText: "Drahtgewebe ansehen"
    }, {
      name: "Sinterdrahtgewebe",
      description: "Mehrlagige Sinterlaminate für formstabile, poröse Filtermedien.",
      range: "1–200 µm",
      image: siteImage("7c6fe4-sintered-multilayer-m-7efb42.webp"),
      imageAlt: "Fünflagige poröse Sintermetall-Filterplatte aus Edelstahl",
      href: "/SinteredWireMeshPage",
      viewText: "Sintergewebe ansehen"
    }, {
      name: "Filterkomponenten",
      description: "Ronden, Zylinder, Siebpakete und Filterkörbe aus Drahtgewebe.",
      image: siteImage("7c6fe4-fabricated-filter-mes-cfe21c.webp"),
      imageAlt: "Konfektionierte Filterronden, Zylinderrohre und Siebpakete",
      href: "/FilterComponentsPage",
      viewText: "Komponenten ansehen"
    }, {
      name: "Spaltsiebe (Keilspaltsiebe)",
      description: "Profilsiebe mit kontinuierlichem Spalt, Siebrohre und Siebböden.",
      range: "Slot 0.02–5.0 mm",
      image: siteImage("7c6fe4-wedge-wire-screen-pan-4aec38.webp"),
      imageAlt: "Spaltsiebboden mit kontinuierlichem Spaltprofil",
      href: "/WedgeWireScreenPage",
      viewText: "Spaltsiebe ansehen"
    }]
  },
  fr: {
    eyebrow: "Gammes de fabrication",
    heading: "Nos gammes de produits",
    lead: "Notre gamme couvre quatre catégories de produits : toiles métalliques tissées, toiles métalliques frittées, composants de filtration et crépines à fil profilé.",
    viewProducts: "Voir les produits",
    cards: [{
      name: "Toiles métalliques tissées",
      description: "Tissus carrés et toiles reps de précision pour la filtration et le tamisage.",
      range: "1–635 mesh · wire 0.020–2.0 mm",
      image: "https://mutualfilter.com/wp-content/uploads/2026/10/plain-dutch-weave-mesh1.jpg",
      imageAlt: "Rouleau de toile métallique en acier inoxydable et détail de maille",
      href: "/WovenWireMeshPage",
      viewText: "Voir toiles tissées"
    }, {
      name: "Toiles métalliques frittées",
      description: "Stratifiés multicouches frittés pour médias filtrants rigides et poreux.",
      range: "1–200 µm",
      image: siteImage("7c6fe4-sintered-multilayer-m-7efb42.webp"),
      imageAlt: "Plaque filtrante poreuse en toile métallique frittée 5 couches",
      href: "/SinteredWireMeshPage",
      viewText: "Voir toiles frittées"
    }, {
      name: "Composants de filtration",
      description: "Disques, cylindres, packs filtrants et paniers façonnés à partir de toile.",
      image: siteImage("7c6fe4-fabricated-filter-mes-cfe21c.webp"),
      imageAlt: "Disques filtrants, cartouches cylindriques et packs de toiles",
      href: "/FilterComponentsPage",
      viewText: "Voir composants"
    }, {
      name: "Crépines à fil profilé",
      description: "Grilles, tubes et panneaux à fente continue en fil trapézoïdal.",
      range: "Slot 0.02–5.0 mm",
      image: siteImage("7c6fe4-wedge-wire-screen-pan-4aec38.webp"),
      imageAlt: "Panneau de crépine à fil profilé à fente continue",
      href: "/WedgeWireScreenPage",
      viewText: "Voir fil profilé"
    }]
  },
  pt: {
    eyebrow: "Escopo de produtos",
    heading: "O que fornecemos",
    lead: "Nossa linha cobre quatro categorias de produtos: malha de arame tecida, malha de arame sinterizada, componentes de filtração e telas de arame em cunha.",
    viewProducts: "Ver produtos",
    cards: [{
      name: "Malha de arame tecida",
      description: "Telas quadradas e holandesas de precisão para filtração e peneiramento.",
      range: "1–635 mesh · wire 0.020–2.0 mm",
      image: "https://mutualfilter.com/wp-content/uploads/2026/10/plain-dutch-weave-mesh1.jpg",
      imageAlt: "Rolo de malha de arame tecida em aço inoxidável e detalhe da abertura",
      href: "/WovenWireMeshPage",
      viewText: "Ver malha tecida"
    }, {
      name: "Malha de arame sinterizada",
      description: "Laminados sinterizados multicamadas para meios filtrantes rígidos e porosos.",
      range: "1–200 µm",
      image: siteImage("7c6fe4-sintered-multilayer-m-7efb42.webp"),
      imageAlt: "Placa porosa de malha sinterizada de aço inoxidável em 5 camadas",
      href: "/SinteredWireMeshPage",
      viewText: "Ver malha sinterizada"
    }, {
      name: "Componentes de filtração",
      description: "Discos, cilindros, pacotes filtrantes e cestos fabricados a partir de telas.",
      image: siteImage("7c6fe4-fabricated-filter-mes-cfe21c.webp"),
      imageAlt: "Discos de filtração, tubos cilíndricos e pacotes de telas conformados",
      href: "/FilterComponentsPage",
      viewText: "Ver componentes"
    }, {
      name: "Telas de arame em cunha",
      description: "Telas, tubos e painéis de ranhura contínua com perfil trapezoidal.",
      range: "Slot 0.02–5.0 mm",
      image: siteImage("7c6fe4-wedge-wire-screen-pan-4aec38.webp"),
      imageAlt: "Painel de tela de arame em cunha com ranhura contínua",
      href: "/WedgeWireScreenPage",
      viewText: "Ver telas em cunha"
    }]
  }
};
const categoryIcons = [Grid3X3, Layers, Disc, Filter];
export default function WhatWeSupply() {
  const {
    lang
  } = useLanguage();
  const current = pick(lang, copy);
  return <section id="whatwesupply" data-nav="light" className="bg-background text-foreground border-b border-border mf-section">
      <div className="mf-container">
        {/* Section Header */}
        <Reveal>
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 font-mono mf-caption uppercase tracking-wider text-primary font-semibold mb-3">
              <span className="h-1.5 w-1.5 rounded-full bg-primary" />
              <span>{current.eyebrow}</span>
            </div>

            <h2 className="mf-h2 text-foreground">
              {current.heading}
            </h2>

            <p className="mt-4 mf-lead text-muted-foreground leading-relaxed">
              {current.lead}
            </p>
          </div>
        </Reveal>

        {/* 4-Card Category Grid: lg: 4 cols, sm: 2 cols, 1 col mobile */}
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {current.cards.map((card, idx) => {
          const Icon = categoryIcons[idx];
          return <Reveal key={idx} delay={Math.min(idx * 60, 300)}>
                <div className="rounded-lg border border-border bg-card overflow-hidden flex flex-col justify-between h-full mf-hover-lift">
                  {/* Category Preview Image */}
                  <div className="mf-card-media mf-ratio-card">
                    <img src={card.image} alt={card.imageAlt} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                    <div className="absolute top-2.5 right-2.5 size-8 rounded-md bg-card border border-border flex items-center justify-center text-primary">
                      <Icon className="size-4" />
                    </div>
                  </div>

                  <div className="p-5 flex-1 flex flex-col justify-between">
                    <div>
                      <h3 className="mf-h3 text-card-foreground">
                        {card.name}
                      </h3>

                      <p className="mt-2 text-sm sm:text-base text-muted-foreground leading-relaxed">
                        {card.description}
                      </p>

                      {card.range ? <div className="mt-3 pt-2.5 border-t border-border">
                          <span className="font-mono mf-caption sm:text-sm font-medium text-foreground tracking-tight block">
                            {card.range}
                          </span>
                        </div> : null}
                    </div>

                    <div className="mt-5 pt-2">
                      <Link to={card.href} className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline underline-offset-4">
                        <span>{card.viewText}</span>
                        <ArrowRight className="size-4" />
                      </Link>
                    </div>
                  </div>
                </div>
              </Reveal>;
        })}
        </div>

        {/* Action Button Below Grid */}
        <Reveal delay={200}>
          <div className="mt-10 flex justify-start">
            <Button asChild size="lg" className="rounded-md">
              <Link to="/ProductsPage" className="inline-flex items-center gap-2 font-medium">
                <span>{current.viewProducts}</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </div>
        </Reveal>
      </div>
    </section>;
}